import { useEffect, useRef, useState } from "react";
import TextFieldCustom from "../../../../component/elements/textfieldCustom";
import CustomButton from "../../../../component/elements/CustomButton/CustomButton";
import { storeDetailsRules } from "../../../../utils/formValidation/validationRules";
import { validateForm } from "../../../../utils/formValidation/validation";
import { getLatLngFromZip, reverseGeocode } from "../../../../utils/latLongFetch";
import axiosInstance from "../../../../configuration/request";
import MapComponent from "../../../../component/mapComponent";
import { FaLocationDot } from "react-icons/fa6";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { useDispatch, useSelector } from "react-redux";
import { editUser } from "../../../../utils/updateUser";
import {
  buildPersonalInfoPayload,
  buildStoreDetailsPayload,
} from "../../../../utils/buildUserPayload";
import { clearAll, setApprovalDetails, setLoginDetails } from "../../../../Redux/Slices/counterSlice";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import BackButton from "../../../../component/backButton";
import UploadDoc from "../../../../component/uploadDoc";

const documents = [
  {
    heading: "Upload Industry License",
    documentType: "PDF, JPEG or JPG",
    name: "industry_license",
  },
  {
    heading: "Upload Shop Image",
    documentType: "PDF, JPEG or JPG",
    name: "shop_image",
  },
];

const ShopDetails = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    storeName: "",
    storeContactNumber: "",
    storeLicenseNumber: "",
    addressLine1: "",
    // addressLine2: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
    latitude: "",
    longitude: "",
    coordinates: null,
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [missingDocs, setMissingDocs] = useState([]);
  const requiredDocs = ["industry_license", "shop_image"];
  const [isMapOpen, setIsMapOpen] = useState(false);
  const formDataWhenMapOpenedRef = useRef({
    coordinates: null,
    latitude: "",
    longitude: "",
    addressLine1: "",
    landmark: "",
    city: "",
    state: "",
    zip: "",
  });
  const showSnackbar = useSnackbar();
  const { personalDetails } = useSelector((state) => state.counter);
  const { authDetails } = useSelector((state) => state.counter);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!authDetails?.otpVerified) {
      navigate("/auth/account");
    }
  }, []);

  useEffect(() => {
    const zip = (formData.zip || "").toString().trim();
    if (zip.length < 5) {
      setFormData((prev) => (prev.latitude && prev.longitude && !prev.coordinates ? { ...prev, latitude: "", longitude: "" } : prev));
      return;
    }
    getLatLngFromZip(zip).then((loc) => {
      setFormData((prev) => {
        if (prev.coordinates) return prev;
        if (loc) return { ...prev, latitude: loc.latitude, longitude: loc.longitude };
        return { ...prev, latitude: "", longitude: "" };
      });
    });
  }, [formData.zip]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const hasValidLocation =
    formData.coordinates != null ||
    (formData.latitude && formData.longitude);

  const handleSubmit = async () => {
    if (!hasValidLocation) {
      showSnackbar("Please pin your location on the map or enter a valid zip code.", "error");
      return;
    }
    const errors = validateForm(formData, storeDetailsRules);
    setFormErrors(errors);
    const uploadedNames = uploadedFiles.map((file) => file.name);
    const missing = requiredDocs.filter((doc) => !uploadedNames.includes(doc));
    setMissingDocs(missing);
    if (Object.keys(errors).length === 0 && missing.length <= 0) {
      let payloadData = {
        ...buildPersonalInfoPayload(personalDetails),
        ...buildStoreDetailsPayload({
          storeDetails: formData,
          uploadedDocs: uploadedFiles ?? [],
        }),
      };
      
      const res = await editUser(payloadData, authDetails.userId, showSnackbar);
      payloadData.userType = res?.data?.userType;
      dispatch(setApprovalDetails(payloadData));
       dispatch(setLoginDetails(authDetails));
      dispatch(clearAll());
      navigate(`/auth/approval`);
    } else {
      showSnackbar(
        "Please complete all required fields before continuing.",
        "error"
      );
    }
  };

  const revertToMapOpenedState = () => {
    const initial = formDataWhenMapOpenedRef.current;
    setFormData((prev) => ({
      ...prev,
      coordinates: initial.coordinates,
      latitude: initial.latitude,
      longitude: initial.longitude,
      addressLine1: initial.addressLine1,
      landmark: initial.landmark,
      city: initial.city,
      state: initial.state,
      zip: initial.zip,
    }));
    setIsMapOpen(false);
  };

  const handleFileSelect = async (file, file_name) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        "/customer/upload/file",
        formData
      );
      const fileUrl = response.data?.url || "";

      setUploadedFiles((prev) => {
        const others = prev.filter((doc) => doc.name !== file_name);
        return [...others, { name: file_name, fileUrl }];
      });
      setMissingDocs((prev) => prev.filter((doc) => doc !== file_name));
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-85px)] overflow-y-auto">
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto bg-white rounded-2xl py-4">
        <div className="w-full max-w-4xl px-8 mt-3 flex items-center">
          <BackButton onClick={() => navigate("/auth/register")} />
        </div>
        <div className="text-2xl font-semibold mb-1">
          Register Your account
        </div>
        <div className="text-sm mb-4">Enter your details to Register</div>

        <div className="w-full max-w-4xl  px-8 mt-6">
          {/* Section: Basic Details */}
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Shop Details
            </div>
            <div className=" grid  grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3">
              <TextFieldCustom
                label="Store Name*"
                value={formData.storeName}
                onChange={handleChange("storeName")}
                error={!!formErrors.storeName}
                helperText={formErrors.storeName || " "}
                fullWidth
              />
              <div className="mb-5 md:mb-0">
                <PhoneInput
                  country={"us"}
                  value={formData.storeContactNumber}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      storeContactNumber: value,
                    }));
                    setFormErrors((prev) => ({
                      ...prev,
                      storeContactNumber: "",
                    }));
                  }}
                  inputClass="w-full py-2 pl-12 pr-4 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#3383B2]"
                  containerClass="w-full"
                  inputStyle={{ width: "100%", height: "53px" }}
                  disableCountryCode={false}
                  disableDropdown={false}
                />
                {formErrors.storeContactNumber && (
                  <span className="text-xs text-red-600 mt-1 ml-1">
                    {formErrors.storeContactNumber}
                  </span>
                )}
              </div>
              <TextFieldCustom
                label="EIN*"
                fullWidth
                value={formData.storeLicenseNumber}
                onChange={handleChange("storeLicenseNumber")}
                error={!!formErrors.storeLicenseNumber}
                helperText={formErrors.storeLicenseNumber || " "}
              />
            </div>
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Address Details
            </div>
            <div className="mt-2">
              <TextFieldCustom
                label="Address*"
                fullWidth
                value={formData.addressLine1}
                onChange={handleChange("addressLine1")}
                error={!!formErrors.addressLine1}
                helperText={formErrors.addressLine1 || " "}
                heightClass={"custom-textarea-height"}
                multiline
                rows={2}
              />
            </div>
            <div className=" grid  grid-cols-1 sm:grid-cols-2 gap-y-0 gap-x-3 ">
              <div className="mb-5 md:mb-0">
                <TextFieldCustom
                  label="Unit"
                  fullWidth
                  value={formData.landmark}
                  onChange={handleChange("landmark")}

                />
              </div>


              <TextFieldCustom
                label="City/Town*"
                fullWidth
                value={formData.city}
                onChange={handleChange("city")}
                error={!!formErrors.city}
                helperText={formErrors.city || " "}
              />

              <TextFieldCustom
                label="State*"
                fullWidth
                value={formData.state}
                onChange={handleChange("state")}
                error={!!formErrors.state}
                helperText={formErrors.state || " "}
              />

              <TextFieldCustom
                label="Zip code*"
                fullWidth
                value={formData.zip}
                onChange={handleChange("zip")}
                error={!!formErrors.zip}
                helperText={formErrors.zip || " "}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <button
              type="button"
              onClick={() => {
                formDataWhenMapOpenedRef.current = {
                  coordinates: formData.coordinates,
                  latitude: formData.latitude,
                  longitude: formData.longitude,
                  addressLine1: formData.addressLine1 ?? "",
                  landmark: formData.landmark ?? "",
                  city: formData.city ?? "",
                  state: formData.state ?? "",
                  zip: formData.zip ?? "",
                };
                setIsMapOpen(true);
              }}
              className="text-[#3b5998] hover:underline inline-flex items-center gap-1 text-sm font-medium"
            >
              <FaLocationDot className="text-lg" /> Pin your precise location on map
            </button>
            {formData.coordinates != null && (
              <span className="text-xs text-gray-500">
                Location set ({Number(formData.coordinates.lat).toFixed(5)}, {Number(formData.coordinates.lng).toFixed(5)})
              </span>
            )}
            {!formData.coordinates && formData.zip?.length >= 5 && formData.latitude && formData.longitude && (
              <span className="text-xs text-green-600">Zip verified</span>
            )}
          </div>
          {!hasValidLocation && (
            <p className="text-amber-600 text-sm mt-1">
              Pin your location on the map or enter a valid zip code to continue.
            </p>
          )}
          {isMapOpen && (
            <div className="mt-2 mb-4 rounded-lg overflow-hidden border border-gray-200">
              <div className="flex justify-between items-center px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                <span className="text-sm text-gray-600">Click on the map to set pin, then confirm or close</span>
                <button
                  type="button"
                  onClick={revertToMapOpenedState}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded"
                  aria-label="Close map"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <MapComponent
                isOpen={isMapOpen}
                address={[formData.addressLine1, formData.city, formData.state, formData.zip].filter(Boolean).join(", ")}
                onSelect={async (coords) => {
                  const address = await reverseGeocode(coords.lat, coords.lng);
                  setFormData((prev) => ({
                    ...prev,
                    coordinates: coords,
                    latitude: coords.lat,
                    longitude: coords.lng,
                    ...(address && {
                      addressLine1: address.addressLine1 ?? prev.addressLine1,
                      landmark: address.landmark ?? prev.landmark,
                      city: address.city ?? prev.city,
                      state: address.state ?? prev.state,
                      zip: address.zip ?? prev.zip,
                    }),
                  }));
                }}
                height="320px"
              />
              <div className="flex justify-end gap-2 p-2 bg-gray-50">
                <CustomButton
                  onClick={revertToMapOpenedState}
                  customeClass="primary-custom-button h-[36px] px-3"
                >
                  Close
                </CustomButton>
                <CustomButton
                  onClick={() => setIsMapOpen(false)}
                  customeClass="gradient-button h-[36px] px-3"
                >
                  Confirm location
                </CustomButton>
              </div>
            </div>
          )}

          {/* Section: document */}
          <div className="mt-6 mb-3">
            <div className="text-sm font-semibold text-gray-700 mb-3">
              Upload Documents
            </div>
            <div className="flex gap-3 flex-wrap">
              {documents.map((doc, idx) => {
                const uploadedDoc = uploadedFiles.find(
                  (f) => f.name === doc.name
                );
                const hasError = missingDocs.includes(doc.name);

                return (
                  <div key={idx} className="flex flex-col">
                    <UploadDoc
                      doc_name={doc.name}
                      heading={doc.heading}
                      documentType={doc.documentType}
                      onFileSelect={handleFileSelect}
                      fileUploaded={!!uploadedDoc}
                      fileUrl={uploadedDoc?.fileUrl}
                      hasError={hasError}
                    />
                    {hasError && (
                      <span className="text-xs text-[#d32f2f] mt-1 ml-1">
                        {doc.name} is required.
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-2 pb-6">
            <CustomButton
              onClick={handleSubmit}
              disabled={!hasValidLocation}
              customeClass={`auth-button w-[300px] h-[45px] ${!hasValidLocation ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Submit
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDetails;
